import { get, post } from '@api/api';
import QueryKeys from '@api/index';
import API_ROUTES from '@api/routes';
import {
	useMutation,
	useQueries,
	useQuery,
	useQueryClient
} from '@tanstack/react-query';
import { AnalyticsActionName } from '@utils/analytics';
import { downloadAsFile } from '@utils/api';
import useAnalytics from 'hooks/useAnalytics';
import { IConnectorDefaults, IConnectors } from 'types/connectors';
import {
	IAuthMethodsSettings,
	ISSOMappings,
	ISSOSettings,
	ISwitchableSettings
} from 'types/settings';

const METADATA_FILE_NAME = 'metadata.xml';
const CERT_FILE_NAME = 'cert.pem';

const authenticationMethods = async () => {
	const res = await get<IAuthMethodsSettings>(
		API_ROUTES.v1.authenticationMethods.get
	);
	return res.data;
};

const connectorsDefaultTemplates = async () => {
	const res = await get<IConnectorDefaults>(API_ROUTES.v1.providers.defaults);
	return res.data;
};

const customConnectors = async () => {
	const res = await get<IConnectors>(API_ROUTES.v1.providers.data);
	return res.data;
};

const getSSOSettings = async (tenantId: string) => {
	const res = await get<ISSOSettings>(
		API_ROUTES.v1.tenants.settings.saml(tenantId).get
	);
	return res.data;
};

const getSSOMappings = async (tenantId: string) => {
	const res = await get<ISSOMappings>(
		API_ROUTES.v1.tenants.settings.saml(tenantId).mappings
	);
	return res.data;
};

export const downloadSSOMetadata = async (tenantId?: string) => {
	const res = await get(
		API_ROUTES.v1.tenants.settings.saml(tenantId!).metadata,
		{
			responseType: 'blob'
		}
	);
	downloadAsFile(res.data as Blob, METADATA_FILE_NAME);
};

export const downloadSSOCertificate = async (tenantId?: string) => {
	const res = await get(
		API_ROUTES.v1.tenants.settings.saml(tenantId!).certificate,
		{
			responseType: 'blob'
		}
	);
	downloadAsFile(res.data as Blob, CERT_FILE_NAME);
};

export const useAuthenticationMethods = () =>
	useQuery([QueryKeys.authenticationMethods], () => authenticationMethods());

export const useCustomProviders = () =>
	useQueries({
		queries: [
			{
				queryKey: [QueryKeys.defaultTemplates],
				queryFn: connectorsDefaultTemplates
			},
			{
				queryKey: [QueryKeys.customConnectors],
				queryFn: customConnectors
			}
		]
	});

export const useUpdateSettingsMutation = <T extends ISwitchableSettings>(
	route: string,
	onSuccess: (currData: IAuthMethodsSettings, data: T) => void
) => {
	const queryClient = useQueryClient();
	const track = useAnalytics();
	return useMutation<T, unknown, T>(
		async (value) => {
			const res = await post<T>(route, value);
			return res.data;
		},
		{
			onSuccess: (data) => {
				const authMethods = queryClient.getQueryData<IAuthMethodsSettings>([
					QueryKeys.authenticationMethods
				]);
				if (authMethods) {
					onSuccess(authMethods, data);
					queryClient.setQueryData(
						[QueryKeys.authenticationMethods],
						authMethods
					);
				}

				track(AnalyticsActionName.authMethodsSettings, {
					route,
					data
				});
			}
		}
	);
};

export const useUpdateProvidersMutation = <T extends IConnectors>() => {
	const queryClient = useQueryClient();
	return useMutation<T, unknown, T>(
		async (value) => {
			const res = await post<T>(API_ROUTES.v1.providers.data, value);
			return res.data;
		},
		{
			onSuccess: (data) => {
				const res = queryClient.getQueryData<IConnectors>([
					QueryKeys.customConnectors
				]);
				if (res) {
					queryClient.setQueryData([QueryKeys.customConnectors], data);
				}
			}
		}
	);
};

export const useTenantSSOSettings = (tenantId: string) =>
	useQuery([`tenantSSO_${tenantId}`], () => getSSOSettings(tenantId), {
		enabled: !!tenantId,
		staleTime: 1000 * 60 * 2 // 2 minutes
	});

export const useUpdateTenantSSOSettings = <T = ISSOSettings>(
	tenantId: string
) => {
	const queryClient = useQueryClient();
	return useMutation<T, unknown, T>(
		async (value) => {
			const res = await post<T>(
				API_ROUTES.v1.tenants.settings.saml(tenantId).set,
				value
			);
			return res.data;
		},
		{
			onSuccess: (data) => {
				queryClient.setQueryData([`tenantSSO_${tenantId}`], data);
			}
		}
	);
};

export const useTenantSSOMappings = (tenantId: string) =>
	useQuery(
		[QueryKeys.tenantSSOMappings, tenantId],
		() => getSSOMappings(tenantId),
		{
			enabled: !!tenantId,
			staleTime: 1000 * 60 * 2 // 2 minutes
		}
	);

export const useUpdateTenantSSOGroupMappings = (tenantId: string) => {
	const queryClient = useQueryClient();
	return useMutation<ISSOMappings, unknown, ISSOMappings>(
		async (value) => {
			const res = await post<ISSOMappings>(
				API_ROUTES.v1.tenants.settings.saml(tenantId).mappings,
				value
			);
			return res.data;
		},
		{
			onSuccess: (data) => {
				queryClient.setQueryData([QueryKeys.tenantSSOMappings, tenantId], data);
			}
		}
	);
};
